import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MailCheck, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function VerifyEmail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyEmail, resendCode } = useAuth();
  const email = location.state?.email || "";
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const noticeShown = useRef(false);

  useEffect(() => {
    if (!email) {
      toast.error(t("auth.verify.noEmail"));
      navigate("/register");
      return;
    }
    if (location.state?.notVerified && !noticeShown.current) {
      noticeShown.current = true;
      toast.message(t("auth.verify.notVerifiedTitle"), { description: t("auth.verify.notVerifiedBody") });
    }
  }, [email, location.state, navigate, t]);

  const doResend = async (silent = false) => {
    setResending(true);
    try {
      await resendCode(email);
      if (!silent) toast.success(t("auth.verify.resent"));
    } catch (_) {
      if (!silent) toast.error(t("common.error"));
    } finally {
      setResending(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return;
    setLoading(true);
    try {
      const user = await verifyEmail(email, code);
      toast.success(t("auth.verify.success"));
      navigate("/dashboard");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const detailStr = typeof detail === "string" ? detail : "";
      if (/expir/i.test(detailStr)) {
        setCode("");
        toast.error(t("auth.verify.expired"));
        doResend(true);
      } else {
        toast.error(detailStr || t("auth.verify.wrongCode"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <div className="bg-white rounded-2xl border border-stone-border shadow-sm p-8" data-testid="verify-email-page">
        <div className="flex flex-col items-center text-center mb-6">
          <div className="w-12 h-12 rounded-xl bg-brand flex items-center justify-center mb-3">
            <MailCheck className="w-6 h-6 text-white" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-stone-text">{t("auth.verify.title")}</h1>
          <p className="text-stone-muted text-sm mt-1">
            {t("auth.verify.subtitle")}
          </p>
          <p className="font-semibold text-stone-text text-sm mt-0.5" data-testid="verify-email-address">{email}</p>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="verify-form">
          <div>
            <label className="block text-sm font-medium text-stone-sub mb-1">{t("auth.verify.codeLabel")}</label>
            <input
              data-testid="verify-code-input"
              inputMode="numeric"
              autoFocus
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="••••••"
              className="w-full px-4 py-3 rounded-xl border border-stone-border text-center text-2xl font-heading tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-brand"
            />
          </div>
          <button
            data-testid="verify-submit"
            type="submit"
            disabled={loading || code.length !== 6}
            className="w-full py-3 rounded-xl bg-brand text-white font-semibold hover:bg-brand-hover transition-colors disabled:opacity-60"
          >
            {loading ? t("auth.verify.verifying") : t("auth.verify.verifyBtn")}
          </button>
        </form>

        <div className="text-center mt-5">
          <button
            data-testid="verify-resend"
            onClick={() => doResend(false)}
            disabled={resending}
            className="text-brand font-semibold text-sm hover:underline disabled:opacity-60"
          >
            {resending ? t("auth.verify.resending") : t("auth.verify.resendBtn")}
          </button>
          <p className="text-xs text-stone-muted mt-3 flex items-center justify-center gap-1">
            <ShieldCheck className="w-3.5 h-3.5" /> {t("auth.verify.checkInbox")}
          </p>
        </div>
      </div>
    </div>
  );
}
